import { Color, Spacing } from '@persona-id/design-tokens';
import { WidgetProps } from '@rjsf/utils';
import { Suspense, useCallback } from 'react';
import styled from 'styled-components';

import DraggableDivider from 'dashboard/components/WorkflowControlPanel/DraggableDivider';
import { filterErrorsForField } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/utils';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import Sticker from 'design-system/components/Sticker';
import useToggle from 'lib/hooks/useToggle';

const Clickable = styled.div`
  cursor: pointer;
`;

const StyledCodeHighlight = styled(CodeHighlight)<{ hasError: boolean }>`
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.color.variants.danger.default : theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const StyledSticker = styled(Sticker)`
  position: absolute;
  top: -${Spacing[3]};
  left: ${Spacing[4]};
  border: 1px solid ${Color.CorePurple200};
`;

const EditorContainer = styled.div`
  height: 100%;
`;

export function HtmlEditorWithPreviewWidget({
  idPrefix,
  idSeparator,
  readonly,
  name,
  value,
  formContext,
  onChange,
}: WidgetProps) {
  const [isOpenedHoverPanel, { enable: openHoverPanel, disable: closeHoverPanel }] =
    useToggle(false);

  const id = idPrefix ? [idPrefix, name].join(idSeparator) : name;
  const errorsForField = filterErrorsForField(formContext.errorsForStep, id);

  const handleChange = useCallback(
    (value: string | undefined) => {
      onChange(value === '' ? undefined : value);
    },
    [onChange],
  );

  return (
    <>
      <Suspense fallback={<p>Loading Previewer</p>}>
        <Clickable
          id='params-send-email__body-html'
          onClick={openHoverPanel}
          aria-label='Open an editor to edit email HTML body'
        >
          <StyledCodeHighlight language='handlebars' hasError={errorsForField.length > 0} light>
            {value?.split('\n').slice(0, 15).join('\n')}
          </StyledCodeHighlight>
        </Clickable>
      </Suspense>
      {isOpenedHoverPanel && (
        <MaximizablePanel
          title='Email Previewer'
          maximized
          onOutsideClickAndEscPress={closeHoverPanel}
        >
          {(fullScreen: boolean) => (
            <div style={{ height: fullScreen ? 'calc(100% - 48px)' : '450px' }}>
              <DraggableDivider
                orientation='horizontal'
                firstComponent={
                  <EditorContainer>
                    <CodeEditor
                      code={value}
                      language='html'
                      readOnly={readonly}
                      onChange={handleChange}
                      disableDebounce
                    />
                  </EditorContainer>
                }
                secondComponent={
                  <div style={{ height: '100%', position: 'relative' }}>
                    <StyledSticker variant='primary'>Preview</StyledSticker>
                    <iframe srcDoc={value} sandbox='' height='100%' width='100%' />
                  </div>
                }
                firstMinHeight='10px'
                secondMinHeight='10px'
                firstStyle={{ overflow: 'hidden' }}
              />
            </div>
          )}
        </MaximizablePanel>
      )}
    </>
  );
}
